import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useRangeKey } from '../TimeRangeControl';
import { fetchSectors } from '../api';
import ChartTooltip from '../charts/ChartTooltip';
import { colorFor } from '../util';
import { BuiltAt, QUERY_OPTIONS, TabStatus } from './_shared';

export default function SectorsTab() {
  const range = useRangeKey();
  const q = useQuery({
    queryKey: ['trends', 'sectors', range],
    queryFn: () => fetchSectors(range),
    ...QUERY_OPTIONS,
  });

  // Build a stacked-bar dataset: one row per year, one column per sector.
  const { rows, sectors } = useMemo(() => {
    if (!q.data) return { rows: [], sectors: [] as string[] };
    const sectorSet = new Set<string>();
    for (const counts of Object.values(q.data.yearly_sector_counts)) {
      for (const k of Object.keys(counts)) sectorSet.add(k);
    }
    const sectorList = Array.from(sectorSet).sort();
    const years = Object.keys(q.data.yearly_sector_counts).sort();
    const out = years.map((y) => {
      const row: Record<string, number | string> = { year: y };
      for (const s of sectorList) {
        row[s] = q.data!.yearly_sector_counts[y]?.[s] ?? 0;
      }
      return row;
    });
    return { rows: out, sectors: sectorList };
  }, [q.data]);

  return (
    <section className="trends-tab-content trends-sectors">
      <TabStatus
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        data={q.data}
        emptyHint="Sectors uses a v1 keyword heuristic over entities_v2 ORGANIZATION context windows. The real classifier is deferred — run the trends_snapshot Dagster asset once IOC extraction has populated context_text."
      >
        {q.data && (
          <>
            <BuiltAt at={q.data.built_at} />
            <div className="trends-sector-banner">
              <strong>v1 heuristic.</strong> Sector attribution comes from a short
              keyword list scanned over entities_v2 ORGANIZATION context windows.
              A real classifier is on the roadmap.
            </div>
            <h2 className="trends-section-title">
              Victim sector mentions by year ({rows.length} years · {sectors.length} sectors)
            </h2>
            {rows.length === 0 ? (
              <div className="trends-chart-empty">No sector matches in this range.</div>
            ) : (
              <ResponsiveContainer width="100%" height={380}>
                <BarChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="year" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {sectors.map((s, i) => (
                    <Bar
                      key={s}
                      dataKey={s}
                      stackId="sectors"
                      fill={colorFor(i)}
                      isAnimationActive={false}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </>
        )}
      </TabStatus>
    </section>
  );
}
